import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Broadcast, Sparkle, PaperPlaneRight, WhatsappLogo, LinkedinLogo, FacebookLogo, InstagramLogo, ChatCircleDots, Copy, ChartLineUp, EnvelopeSimple, Eye, CursorClick, ChatCircle, CheckCircle, Warning, Envelope, Clock } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { apiErrMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const REGIONS = ["North India", "South India", "East India", "West India", "East Africa", "West Africa", "LATAM", "GCC", "Southeast Asia"];
const LANGS = [
  { v: "en", l: "English" }, { v: "hi", l: "Hindi" }, { v: "es", l: "Spanish" },
  { v: "pt", l: "Portuguese" }, { v: "fr", l: "French" }, { v: "sw", l: "Swahili" }, { v: "ar", l: "Arabic" },
];

export default function AISwarm() {
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [region, setRegion] = useState("North India");
  const [productLine, setProductLine] = useState("Medical equipment and devices");
  const [language, setLanguage] = useState("en");
  const [generating, setGenerating] = useState(false);
  const [pack, setPack] = useState(null); // {email_subject, email_html, whatsapp_text, linkedin_post, quora_answer, target_url, tracking_token}
  const [busyChannel, setBusyChannel] = useState("");
  const [dispatches, setDispatches] = useState([]);
  const [roi, setRoi] = useState(null);
  const [linkedInStatus, setLinkedInStatus] = useState({ connections: [], configured: false });
  const [metaStatus, setMetaStatus] = useState({ connected: false, pages: [] });
  const [selectedMetaPage, setSelectedMetaPage] = useState("");
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestRecipients, setDigestRecipients] = useState([]);
  const [savingDigest, setSavingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadLeads = useCallback(async () => {
    try { const { data } = await api.get("/leads"); setLeads(data || []); }
    catch (e) { toast.error(apiErrMsg(e, "Load leads failed")); }
  }, []);
  const loadDispatches = useCallback(async () => {
    try {
      const [d, r, li, ms] = await Promise.all([
        api.get("/swarm/dispatches", { params: { days: 30 } }),
        api.get("/swarm/roi", { params: { days: 30 } }),
        api.get("/linkedin/status"),
        api.get("/meta/status").catch(() => ({ data: { connected: false, pages: [] } })),
      ]);
      setDispatches(d.data || []); setRoi(r.data || null); setLinkedInStatus(li.data || {});
      setMetaStatus(ms.data || { connected: false, pages: [] });
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadLeads(); loadDispatches(); }, [loadLeads, loadDispatches]);

  // Handle LinkedIn OAuth callback flags
  useEffect(() => {
    const li = searchParams.get("linkedin");
    if (li === "connected") {
      toast.success(`LinkedIn ${searchParams.get("mode") || ""} connected`);
      loadDispatches();
      searchParams.delete("linkedin"); searchParams.delete("mode");
      setSearchParams(searchParams, { replace: true });
    } else if (li === "error") {
      toast.error(`LinkedIn connect failed: ${searchParams.get("reason") || "unknown"}`);
      searchParams.delete("linkedin"); searchParams.delete("reason");
      setSearchParams(searchParams, { replace: true });
    }
    // Handle Meta (Facebook + Instagram) OAuth callback flags
    const meta = searchParams.get("meta");
    if (meta === "connected") {
      toast.success(`Meta connected · ${searchParams.get("pages") || 0} Page(s) available`);
      loadDispatches();
      searchParams.delete("meta"); searchParams.delete("pages");
      setSearchParams(searchParams, { replace: true });
    } else if (meta === "error") {
      toast.error(`Meta connect failed: ${searchParams.get("reason") || "unknown"}`);
      searchParams.delete("meta"); searchParams.delete("reason");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadDispatches]);

  const currentLead = leads.find((l) => l.id === leadId);

  const generate = async () => {
    if (!leadId) { toast.error("Pick a lead first"); return; }
    setGenerating(true);
    setPack(null);
    try {
      const { data } = await api.post("/swarm/generate", { lead_id: leadId, region, product_line: productLine, language });
      setPack(data);
      toast.success("Cross-channel content generated");
    } catch (e) { toast.error(apiErrMsg(e, "Generate failed")); }
    finally { setGenerating(false); }
  };

  const dispatchEmail = async () => {
    if (!pack) return;
    if (!currentLead?.email) { toast.error("Lead has no email"); return; }
    setBusyChannel("email");
    try {
      await api.post("/swarm/dispatch/email", {
        lead_id: leadId, region, product_line: productLine, language,
        subject: pack.email_subject, body_html: pack.email_html, target_url: pack.target_url,
        image_url: pack.image_url || "",
      });
      toast.success("Email dispatched via Gmail");
      loadDispatches();
    } catch (e) { toast.error(apiErrMsg(e, "Email dispatch failed")); }
    finally { setBusyChannel(""); }
  };

  const dispatchWhatsApp = async () => {
    if (!pack) return;
    if (!currentLead?.phone) { toast.error("Lead has no phone"); return; }
    setBusyChannel("whatsapp");
    try {
      const { data } = await api.post("/swarm/dispatch/whatsapp", {
        lead_id: leadId, region, product_line: productLine, language,
        message: pack.whatsapp_text, target_url: pack.target_url,
        image_url: pack.image_url || "",
      });
      if (data.mocked) toast.warning("Bhash credentials not configured yet — message queued in log");
      else toast.success("WhatsApp dispatched via Bhash");
      loadDispatches();
    } catch (e) { toast.error(apiErrMsg(e, "WhatsApp dispatch failed")); }
    finally { setBusyChannel(""); }
  };

  const logManual = async (channel, body) => {
    if (!pack) return;
    setBusyChannel(channel);
    try {
      await navigator.clipboard.writeText(body);
      await api.post("/swarm/dispatch/manual", {
        lead_id: leadId, channel, region, product_line: productLine, language,
        body, target_url: pack.target_url,
      });
      toast.success(`${channel === "linkedin" ? "LinkedIn" : "Quora"} copy in clipboard + logged as dispatched`);
      loadDispatches();
    } catch (e) { toast.error(apiErrMsg(e, "Log failed")); }
    finally { setBusyChannel(""); }
  };

  const markReplied = async (id) => {
    try { await api.post(`/swarm/dispatches/${id}/reply`); toast.success("Marked replied"); loadDispatches(); }
    catch (e) { toast.error(apiErrMsg(e, "Mark failed")); }
  };

  const memberConn = linkedInStatus.connections?.find((c) => c.mode === "member");
  const orgConn = linkedInStatus.connections?.find((c) => c.mode === "organization");

  const connectLinkedIn = async (mode) => {
    try {
      const { data } = await api.get("/linkedin/oauth/init", { params: { mode } });
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(apiErrMsg(e, "Connect failed")); }
  };

  const disconnectLinkedIn = async (mode) => {
    if (!window.confirm(`Disconnect LinkedIn (${mode})?`)) return;
    try { await api.delete("/linkedin/connection", { params: { mode } }); toast.success("Disconnected"); loadDispatches(); }
    catch (e) { toast.error(apiErrMsg(e, "Disconnect failed")); }
  };

  const postToLinkedIn = async () => {
    if (!pack || !memberConn) return;
    setBusyChannel("linkedin");
    try {
      await api.post("/linkedin/post", {
        text: pack.linkedin_post, target_url: pack.target_url, mode: "member", lead_id: leadId,
      });
      toast.success("Posted to LinkedIn");
      loadDispatches();
    } catch (e) { toast.error(apiErrMsg(e, "Post failed")); }
    finally { setBusyChannel(""); }
  };

  // --- Meta (Facebook Page + Instagram Business) ---
  const connectMeta = async () => {
    try {
      const { data } = await api.get("/meta/oauth/init");
      window.location.href = data.auth_url;
    } catch (e) { toast.error(apiErrMsg(e, "Meta connect failed")); }
  };
  const disconnectMeta = async () => {
    if (!window.confirm("Disconnect Meta (Facebook + Instagram)?")) return;
    try { await api.delete("/meta/connection"); toast.success("Meta disconnected"); loadDispatches(); }
    catch (e) { toast.error(apiErrMsg(e, "Disconnect failed")); }
  };
  const refreshMetaAssets = async () => {
    try {
      const { data } = await api.post("/meta/refresh-assets");
      toast.success(`Refreshed · ${data.pages?.length || 0} Page(s)`);
      loadDispatches();
    } catch (e) { toast.error(apiErrMsg(e, "Refresh failed")); }
  };
  const postToMeta = async (channel) => {
    if (!pack) return;
    if (!metaStatus.pages?.length) { toast.error("Connect Meta first"); return; }
    const page = metaStatus.pages.find((p) => p.page_id === selectedMetaPage) || metaStatus.pages[0];
    if (channel === "instagram" && !page.instagram?.ig_user_id) {
      toast.error("This Page has no linked Instagram Business account. Link one in Meta Business Suite, then click ↻");
      return;
    }
    const imageUrl = pack.hero_image_url || pack.image_url || pack.target_url;
    if (!imageUrl || !imageUrl.startsWith("http")) {
      toast.error("No public image URL available on the generated pack — regenerate with a product image");
      return;
    }
    setBusyChannel(channel);
    try {
      await api.post("/meta/post", {
        channel, page_id: page.page_id,
        image_url: imageUrl,
        caption: pack.linkedin_post || pack.email_html || "",
        lead_id: leadId,
      });
      toast.success(`Posted to ${channel === "facebook" ? "Facebook Page" : "Instagram Business"}`);
      loadDispatches();
    } catch (e) { toast.error(apiErrMsg(e, `${channel} post failed`)); }
    finally { setBusyChannel(""); }
  };

  const openDigest = async () => {
    setDigestOpen(true);
    try {
      const [p, r] = await Promise.all([api.get("/digest/preview"), api.get("/digest/prefs")]);
      setDigestPreview(p.data); setDigestRecipients(r.data.recipients || []);
    } catch (e) { toast.error(apiErrMsg(e, "Load digest failed")); }
  };

  const saveDigestPrefs = async () => {
    setSavingDigest(true);
    try {
      await api.put("/digest/prefs", { recipients: digestRecipients });
      toast.success("Recipients saved · digest goes out Mondays 10:00 IST");
    } catch (e) { toast.error(apiErrMsg(e, "Save failed")); }
    finally { setSavingDigest(false); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/digest/send-now", digestRecipients);
      if (data.ok) toast.success(`Digest sent to ${digestRecipients.join(", ")}`);
      else toast.error(`Send failed: ${data.reason || "unknown"} — did you connect Gmail in Admin?`);
    } catch (e) { toast.error(apiErrMsg(e, "Send failed")); }
    finally { setSendingDigest(false); }
  };

  return (
    <Layout title="AI Swarm Outreach" subtitle="Multilingual cross-channel campaigns · trafficked to shelvestech.com">
      {/* ROI band */}
      {roi && (
        <div className="grid grid-cols-2 md:grid-cols-6 border border-black/10 bg-white mb-6" data-testid="swarm-roi-band">
          <Kpi l="Dispatched (30d)" v={roi.total_dispatched} icon={<PaperPlaneRight size={14}/>}/>
          <Kpi l="Opens" v={roi.total_opened} c="#0D9488" icon={<Eye size={14}/>}/>
          <Kpi l="Clicks" v={roi.total_clicked} c="#F4A261" icon={<CursorClick size={14}/>}/>
          <Kpi l="Replies" v={roi.total_replied} c="#0D9488" icon={<ChatCircle size={14}/>}/>
          <Kpi l="Queued" v={roi.total_queued} c="#717680"/>
          <Kpi l="Failed" v={roi.total_failed} c={roi.total_failed > 0 ? "#D90429" : "#717680"}/>
        </div>
      )}

      {/* LinkedIn + Digest bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* LinkedIn card */}
        <div className="border border-black/10 bg-white p-4" data-testid="swarm-linkedin-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <LinkedinLogo size={18} weight="fill" className="text-[#0077B5]"/>
              <span className="font-display font-black text-base tracking-tight">LinkedIn</span>
            </div>
            {!linkedInStatus.configured && (
              <span className="text-[10px] font-mono-shelv uppercase text-[#F4A261]"><Warning size={11} className="inline"/> not configured</span>
            )}
          </div>
          {!linkedInStatus.configured ? (
            <div className="text-[11px] text-[#717680] leading-relaxed" data-testid="li-setup-help">
              <p className="mb-1.5"><b>Setup (5 min):</b></p>
              <ol className="list-decimal pl-4 space-y-0.5">
                <li>Go to <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noreferrer" className="text-[#0077B5] underline font-semibold">linkedin.com/developers/apps</a> → <b>Create app</b></li>
                <li>Fill in App name, associate any LinkedIn Page (create one if none — free), verify</li>
                <li>Products tab → request <b>Sign In with LinkedIn using OpenID Connect</b> + <b>Share on LinkedIn</b> (instant approval)</li>
                <li>Auth tab → copy <b>Client ID</b> + <b>Client Secret</b></li>
                <li>Auth tab → add these Redirect URIs (paste BOTH — LinkedIn supports multiple):
                    <div className="mt-1 space-y-0.5">
                      <code className="block text-[10px] break-all bg-black/5 px-1 py-0.5" data-testid="li-redirect-uri">{linkedInStatus?.redirect_uri || "https://<your-domain>/api/linkedin/oauth/callback"}</code>
                      <code className="block text-[10px] break-all bg-black/5 px-1 py-0.5">https://reyanshcrm.com/api/linkedin/oauth/callback</code>
                    </div>
                </li>
                <li>Send me the Client ID + Client Secret so I can add them to backend/.env, then redeploy</li>
              </ol>
            </div>
          ) : (
            <div className="space-y-2">
              <ConnRow mode="member" label="Personal profile" conn={memberConn} onConnect={connectLinkedIn} onDisconnect={disconnectLinkedIn}/>
              <ConnRow mode="organization" label="Company Page (needs LinkedIn approval)" conn={orgConn} onConnect={connectLinkedIn} onDisconnect={disconnectLinkedIn}/>
            </div>
          )}
        </div>

        {/* Meta (Facebook + Instagram) connection card */}
        <div className="border border-black/10 bg-white p-4" data-testid="swarm-meta-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FacebookLogo size={18} weight="fill" className="text-[#1877F2]"/>
              <InstagramLogo size={18} weight="fill" className="text-[#E1306C]"/>
              <span className="font-display font-black text-base tracking-tight">Meta · Facebook + Instagram</span>
            </div>
            {metaStatus.connected ? (
              <span className="text-[10px] font-mono-shelv uppercase text-[#0D9488]">● Connected</span>
            ) : (
              <span className="text-[10px] font-mono-shelv uppercase text-[#717680]">○ Not connected</span>
            )}
          </div>
          {!metaStatus.connected ? (
            <>
              <p className="text-[12px] text-[#4A4D57] mb-3 leading-relaxed">
                Auto-post AI Swarm product images + captions to your Facebook Pages and Instagram Business accounts.
              </p>
              <button onClick={connectMeta} data-testid="meta-connect-btn" className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90">
                <FacebookLogo size={12} weight="bold"/> Connect Facebook &amp; Instagram
              </button>
            </>
          ) : (
            <div className="space-y-2" data-testid="meta-assets-list">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-[#4A4D57]">Connected as <b>{metaStatus.meta_name || "user"}</b> · {metaStatus.pages?.length || 0} Page(s)</span>
                <div className="flex gap-1">
                  <button onClick={refreshMetaAssets} data-testid="meta-refresh-btn" title="Refresh Pages + IG list" className="px-2 py-1 border border-black/15 text-[10px] font-semibold hover:bg-black hover:text-white">↻</button>
                  <button onClick={disconnectMeta} data-testid="meta-disconnect-btn" className="px-2 py-1 border border-[#D90429]/40 text-[#D90429] text-[10px] font-semibold hover:bg-[#D90429] hover:text-white">Disconnect</button>
                </div>
              </div>
              {(metaStatus.last_sync_at || metaStatus.last_sync_error) && (
                <div data-testid="meta-last-sync" className="text-[10px] text-[#717680] flex items-center gap-2 font-mono uppercase tracking-wider">
                  {metaStatus.last_sync_error ? (
                    <span className="text-[#D90429]">⚠ {metaStatus.last_sync_error.slice(0, 60)}</span>
                  ) : (
                    <>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Auto-synced {metaStatus.last_sync_at ? new Date(metaStatus.last_sync_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      {metaStatus.last_sync_source === "cron" ? " · cron" : ""}
                      <span className="text-[#B8BCC7]">· refresh every 6h</span>
                    </>
                  )}
                </div>
              )}
              {(metaStatus.pages || []).length === 0 ? (
                <div className="text-[11px] text-[#717680] italic p-2 bg-[#FAFBFC] border border-black/5">No Pages found. Ensure your Meta account manages at least one Facebook Page, then click ↻.</div>
              ) : (
                <div className="space-y-1">
                  {metaStatus.pages.map((p) => (
                    <label key={p.page_id} className={`block px-2 py-1.5 border cursor-pointer text-[11px] ${selectedMetaPage === p.page_id || (!selectedMetaPage && p.page_id === metaStatus.pages[0].page_id) ? "border-[#D90429] bg-[#FFE5E9]/40" : "border-black/10 hover:bg-[#FAFBFC]"}`} data-testid={`meta-page-${p.page_id}`}>
                      <input type="radio" name="meta-page" checked={selectedMetaPage === p.page_id || (!selectedMetaPage && p.page_id === metaStatus.pages[0].page_id)} onChange={() => setSelectedMetaPage(p.page_id)} className="mr-1.5"/>
                      <b>{p.page_name}</b>
                      {p.instagram?.ig_user_id ? (
                        <span className="ml-2 text-[10px] text-[#E1306C]">+ IG @{p.instagram.username}</span>
                      ) : (
                        <span className="ml-2 text-[10px] text-[#717680]">· no IG linked</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Weekly digest card */}
        <div className="border border-black/10 bg-white p-4" data-testid="swarm-digest-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Envelope size={18} weight="fill" className="text-[#D90429]"/>
              <span className="font-display font-black text-base tracking-tight">Weekly Monday Digest</span>
            </div>
            <span className="text-[10px] font-mono-shelv uppercase text-[#0D9488]"><Clock size={11} className="inline"/> Mon 10:00 IST</span>
          </div>
          <p className="text-[12px] text-[#4A4D57] mb-3 leading-relaxed">
            Auto-sends every Monday: top channels + WoW comparison + hot leads (who clicked your CTA). Uses your connected Gmail.
          </p>
          <button onClick={openDigest} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#D90429] text-[#D90429] text-xs font-semibold hover:bg-[#D90429] hover:text-white" data-testid="digest-open">
            <Envelope size={12}/> Preview &amp; configure recipients
          </button>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Setup */}
        <section className="border border-black/10 bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-[#D90429] text-white flex items-center justify-center"><Broadcast size={16} weight="bold"/></div>
            <div>
              <div className="font-display text-lg font-black tracking-tighter">Campaign Setup</div>
              <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429]">Step 1 — pick lead &amp; region</div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Lead *</Label>
              <Select value={leadId} onValueChange={setLeadId}>
                <SelectTrigger className="rounded-none" data-testid="swarm-lead-select"><SelectValue placeholder="Pick a lead"/></SelectTrigger>
                <SelectContent>
                  {leads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} {l.company ? `· ${l.company}` : ""} {l.email ? "· ✉" : ""} {l.phone ? "· ☎" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentLead && (
                <div className="mt-2 text-[11px] text-[#717680] leading-tight">
                  <div>📍 {currentLead.location || "no location"}</div>
                  <div>Requirement: {currentLead.requirement || "not stated"}</div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger className="rounded-none" data-testid="swarm-region"><SelectValue/></SelectTrigger>
                  <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="rounded-none" data-testid="swarm-lang"><SelectValue/></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Product line</Label>
              <Input value={productLine} onChange={(e) => setProductLine(e.target.value)} className="rounded-none" data-testid="swarm-product"/>
            </div>
            <button onClick={generate} disabled={!leadId || generating} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50 disabled:cursor-not-allowed" data-testid="swarm-generate">
              <Sparkle size={16} weight="bold"/> {generating ? "Generating with Gemini…" : "Generate Cross-Channel Kit"}
            </button>
          </div>
        </section>

        {/* RIGHT: Generated pack */}
        <section className="border border-black/10 bg-white p-5 min-h-[560px]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-[#F4A261] text-white flex items-center justify-center"><Sparkle size={16} weight="bold"/></div>
            <div>
              <div className="font-display text-lg font-black tracking-tighter">Generated Kit</div>
              <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429]">Step 2 — review, edit, dispatch</div>
            </div>
          </div>
          {!pack && <div className="text-sm text-[#717680] p-8 text-center border border-dashed border-black/10">Pick a lead, then click <b>Generate</b> to see all 4 channel copies here.</div>}
          {pack && (
            <div className="space-y-4">
              {/* Target URL */}
              <div className="border border-[#F4A261]/30 bg-[#F4A261]/5 p-2.5 text-[11px] font-mono-shelv break-all" data-testid="swarm-target-url">
                <span className="text-[#D90429] font-bold">CTA URL →</span> {pack.target_url}
              </div>

              {/* AI-Generated Product Image (Nano Banana) */}
              {pack.image_url ? (
                <div className="border border-black/10 bg-[#FAFBFC] p-3" data-testid="swarm-image-block">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#0D9488]">★ Auto-generated product photo · attached to email + WhatsApp</div>
                    <CopyBtn text={pack.image_url.startsWith("http") ? pack.image_url : `${process.env.REACT_APP_BACKEND_URL}${pack.image_url}`}/>
                  </div>
                  <img
                    src={pack.image_url.startsWith("http") ? pack.image_url : `${process.env.REACT_APP_BACKEND_URL}${pack.image_url}`}
                    alt="AI product photo"
                    className="w-full max-h-[280px] object-contain border border-black/5 bg-white"
                    data-testid="swarm-generated-image"
                  />
                </div>
              ) : (
                <div className="border border-dashed border-black/10 bg-white px-3 py-2 text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680]" data-testid="swarm-image-fallback">
                  ⚠ Product photo generation unavailable this run — email will send without an image
                </div>
              )}

              {/* EMAIL */}
              <ChannelBlock icon={<EnvelopeSimple size={14}/>} title="Email" testid="swarm-email">
                <Input value={pack.email_subject} onChange={(e) => setPack({ ...pack, email_subject: e.target.value })} className="rounded-none mb-2 font-semibold text-sm" data-testid="swarm-email-subject"/>
                <Textarea value={pack.email_html} onChange={(e) => setPack({ ...pack, email_html: e.target.value })} className="rounded-none min-h-[100px] text-[12px] font-mono-shelv" data-testid="swarm-email-body"/>
                <div className="flex gap-2 mt-2">
                  <button onClick={dispatchEmail} disabled={busyChannel === "email" || !currentLead?.email} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="swarm-send-email">
                    <PaperPlaneRight size={12}/> {busyChannel === "email" ? "Sending…" : `Send via Gmail${!currentLead?.email ? " (no email)" : ""}`}
                  </button>
                  <CopyBtn text={`Subject: ${pack.email_subject}\n\n${pack.email_html}`}/>
                </div>
              </ChannelBlock>

              {/* WHATSAPP */}
              <ChannelBlock icon={<WhatsappLogo size={14}/>} title="WhatsApp" testid="swarm-whatsapp">
                <Textarea value={pack.whatsapp_text} onChange={(e) => setPack({ ...pack, whatsapp_text: e.target.value })} className="rounded-none min-h-[70px] text-[12px]" data-testid="swarm-wa-body"/>
                <div className="flex gap-2 mt-2">
                  <button onClick={dispatchWhatsApp} disabled={busyChannel === "whatsapp" || !currentLead?.phone} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0D9488] text-white text-xs font-semibold hover:bg-[#0F766E] disabled:opacity-50" data-testid="swarm-send-wa">
                    <PaperPlaneRight size={12}/> {busyChannel === "whatsapp" ? "Sending…" : `Send via Bhash${!currentLead?.phone ? " (no phone)" : ""}`}
                  </button>
                  <CopyBtn text={pack.whatsapp_text}/>
                </div>
              </ChannelBlock>

              {/* LINKEDIN */}
              <ChannelBlock icon={<LinkedinLogo size={14}/>} title={memberConn ? `LinkedIn post · connected as ${memberConn.name || "you"}` : "LinkedIn post"} testid="swarm-linkedin">
                <Textarea value={pack.linkedin_post} onChange={(e) => setPack({ ...pack, linkedin_post: e.target.value })} className="rounded-none min-h-[80px] text-[12px]" data-testid="swarm-li-body"/>
                <div className="flex gap-2 mt-2">
                  {memberConn ? (
                    <button onClick={postToLinkedIn} disabled={busyChannel === "linkedin"} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0077B5] text-white text-xs font-semibold hover:bg-[#005f8f] disabled:opacity-50" data-testid="swarm-send-li">
                      <PaperPlaneRight size={12}/> {busyChannel === "linkedin" ? "Posting…" : "Post to LinkedIn"}
                    </button>
                  ) : (
                    <button onClick={() => logManual("linkedin", pack.linkedin_post)} disabled={busyChannel === "linkedin"} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-black/20 text-[#0A0A0A] text-xs font-semibold hover:bg-black hover:text-white" data-testid="swarm-send-li">
                      <Copy size={12}/> Copy &amp; log dispatch
                    </button>
                  )}
                </div>
              </ChannelBlock>

              {/* QUORA */}
              <ChannelBlock icon={<ChatCircleDots size={14}/>} title="Quora answer" testid="swarm-quora">
                <Textarea value={pack.quora_answer} onChange={(e) => setPack({ ...pack, quora_answer: e.target.value })} className="rounded-none min-h-[80px] text-[12px]" data-testid="swarm-q-body"/>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => logManual("quora", pack.quora_answer)} disabled={busyChannel === "quora"} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-black/20 text-[#0A0A0A] text-xs font-semibold hover:bg-black hover:text-white" data-testid="swarm-send-q">
                    <Copy size={12}/> Copy &amp; log dispatch
                  </button>
                </div>
              </ChannelBlock>

              {/* META · FACEBOOK PAGE + INSTAGRAM BUSINESS */}
              <ChannelBlock icon={<FacebookLogo size={14}/>} title={metaStatus.connected ? `Meta · post to ${metaStatus.pages?.length || 0} page(s)` : "Meta (connect first)"} testid="swarm-meta">
                <div className="text-[11px] text-[#4A4D57] mb-2 leading-relaxed">
                  Uses the caption in the LinkedIn post box + the AI-generated hero image. Facebook posts land on the selected Page; Instagram posts require an IG Business account linked to that Page.
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => postToMeta("facebook")}
                    disabled={!metaStatus.connected || busyChannel === "facebook"}
                    data-testid="swarm-send-fb"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FacebookLogo size={12}/> {busyChannel === "facebook" ? "Posting…" : "Post to Facebook Page"}
                  </button>
                  <button
                    onClick={() => postToMeta("instagram")}
                    disabled={!metaStatus.connected || busyChannel === "instagram"}
                    data-testid="swarm-send-ig"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }}
                  >
                    <InstagramLogo size={12}/> {busyChannel === "instagram" ? "Posting…" : "Post to Instagram Business"}
                  </button>
                  {!metaStatus.connected && (
                    <button onClick={connectMeta} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#1877F2] text-[#1877F2] text-xs font-semibold hover:bg-[#1877F2] hover:text-white">
                      Connect Meta
                    </button>
                  )}
                </div>
              </ChannelBlock>
            </div>
          )}
        </section>
      </div>

      {/* Dispatch log */}
      <section className="mt-6 border border-black/10 bg-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-black/10">
          <div className="flex items-center gap-2">
            <ChartLineUp size={16} className="text-[#D90429]"/>
            <div className="font-display text-lg font-black tracking-tighter">Dispatch Log · Last 30 days</div>
          </div>
          <div className="text-[11px] font-mono-shelv text-[#717680]">{dispatches.length} rows</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="swarm-log-table">
            <thead className="bg-[#F5F6F8] text-left">
              <tr>{["When","Lead","Channel","Region","Language","Status","Open","Click","Reply"].map(h => (
                <th key={h} className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {dispatches.length === 0 && (
                <tr><td colSpan="9" className="px-3 py-10 text-center text-[#717680] text-sm">No dispatches yet — pick a lead above and generate + send.</td></tr>
              )}
              {dispatches.map((r) => (
                <tr key={r.id} className="border-t border-black/5" data-testid={`swarm-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono-shelv text-[11px] whitespace-nowrap">{(r.dispatched_at || r.created_at).slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 font-semibold">{r.lead_name}</td>
                  <td className="px-3 py-2"><ChannelPill ch={r.channel}/></td>
                  <td className="px-3 py-2 text-[12px]">{r.region}</td>
                  <td className="px-3 py-2 font-mono-shelv text-[11px] uppercase">{r.language}</td>
                  <td className="px-3 py-2"><StatusPill s={r.status}/></td>
                  <td className="px-3 py-2 text-[12px]">{r.opened_at ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-[12px]">{r.clicked_at ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-[12px]">
                    {r.replied_at ? "✓" : (
                      <button onClick={() => markReplied(r.id)} className="text-[#D90429] hover:underline text-[11px]" data-testid={`swarm-mark-reply-${r.id}`}>mark</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Digest preview + recipients dialog */}
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Weekly Monday Digest</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Recipients (comma separated, sent every Monday 10:00 IST via your Gmail)</Label>
              <Input
                value={digestRecipients.join(", ")}
                onChange={(e) => setDigestRecipients(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="info@shelvestech.com, founder@shelvestech.com"
                className="rounded-none"
                data-testid="digest-recipients"
              />
              <div className="flex gap-2 mt-2">
                <button onClick={saveDigestPrefs} disabled={savingDigest} className="px-3 py-1.5 border border-black/20 text-xs font-semibold hover:bg-black hover:text-white disabled:opacity-50" data-testid="digest-save">
                  {savingDigest ? "Saving…" : "Save recipients"}
                </button>
                <button onClick={sendDigestNow} disabled={sendingDigest || digestRecipients.length === 0} className="px-3 py-1.5 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="digest-send-now">
                  {sendingDigest ? "Sending…" : "Send digest now"}
                </button>
              </div>
            </div>
            <div>
              <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-2">Preview · what Monday&apos;s email will look like</div>
              <div className="border border-black/10">
                <div className="p-2 bg-[#F5F6F8] text-[12px] font-semibold border-b border-black/10">Subject: {digestPreview?.subject || "…"}</div>
                <iframe
                  title="digest-preview"
                  srcDoc={digestPreview?.html || "<p style='padding:32px;color:#717680'>Loading…</p>"}
                  className="w-full min-h-[420px] bg-white"
                  sandbox=""
                  data-testid="digest-preview-frame"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDigestOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ConnRow({ mode, label, conn, onConnect, onDisconnect }) {
  return (
    <div className="flex items-center justify-between p-2 bg-[#F5F6F8]" data-testid={`li-conn-${mode}`}>
      <div className="flex items-center gap-2">
        {conn ? <CheckCircle size={14} weight="fill" className="text-[#0D9488]"/> : <Warning size={14} className="text-[#717680]"/>}
        <div>
          <div className="text-[12px] font-semibold">{label}</div>
          {conn && <div className="text-[10px] text-[#717680]">{conn.name} · {conn.email}</div>}
        </div>
      </div>
      {conn ? (
        <button onClick={() => onDisconnect(mode)} className="px-2 py-1 border border-[#D90429] text-[#D90429] text-[10px] font-semibold hover:bg-[#D90429] hover:text-white" data-testid={`li-disconnect-${mode}`}>Disconnect</button>
      ) : (
        <button onClick={() => onConnect(mode)} className="px-2 py-1 bg-[#0077B5] text-white text-[10px] font-semibold hover:bg-[#005f8f]" data-testid={`li-connect-${mode}`}>Connect</button>
      )}
    </div>
  );
}

function ChannelBlock({ icon, title, testid, children }) {
  return (
    <div className="border border-black/10 p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429]">{title}</span>
      </div>
      {children}
    </div>
  );
}

function CopyBtn({ text }) {
  return (
    <button onClick={() => { navigator.clipboard.writeText(text || ""); toast.success("Copied"); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-black/20 text-[#0A0A0A] text-xs font-semibold hover:bg-black hover:text-white">
      <Copy size={12}/> Copy
    </button>
  );
}

function Kpi({ l, v, c = "#0A0A0A", icon }) {
  return (
    <div className="p-4 border-r border-black/5 last:border-r-0">
      <div className="flex items-center gap-1.5 font-mono-shelv text-[9px] tracking-widest uppercase text-[#717680]">{icon}{l}</div>
      <div className="font-display text-2xl font-black tracking-tighter mt-1" style={{ color: c }}>{Number(v).toLocaleString("en-IN")}</div>
    </div>
  );
}

function ChannelPill({ ch }) {
  const styles = { email: "bg-[#D90429]/10 text-[#D90429]", whatsapp: "bg-[#0D9488]/10 text-[#0D9488]", linkedin: "bg-[#0A0A0A]/10 text-[#0A0A0A]", quora: "bg-[#F4A261]/10 text-[#F4A261]" };
  return <span className={`px-2 py-0.5 text-[11px] font-mono-shelv uppercase ${styles[ch] || "bg-black/5 text-[#717680]"}`}>{ch}</span>;
}

function StatusPill({ s }) {
  const colors = { dispatched: "#0D9488", opened: "#0D9488", clicked: "#F4A261", replied: "#0D9488", failed: "#D90429", draft: "#717680" };
  const c = colors[s] || (s?.startsWith("queued") ? "#F4A261" : "#717680");
  return <span className="px-2 py-0.5 text-[11px] font-mono-shelv" style={{ background: `${c}20`, color: c }}>{s}</span>;
}
